import {useEffect, useState} from 'react'

import { Box, BoxProps } from '@mui/system'
import { AppBar, Button, Card, CardActions, CardContent, Grid, IconButton, Paper, TextField, Toolbar, Typography } from '@mui/material'
import PersonalIcon from '@mui/icons-material/PersonOutlineTwoTone'
import ProfessionalIcon from '@mui/icons-material/EngineeringTwoTone';
import SocialIcon from '@mui/icons-material/GroupsTwoTone'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/CheckCircle'
import LaunchIcon from '@mui/icons-material/Launch';
import AddLinkIcon from '@mui/icons-material/AddCircle';

import ConfirmationDialog from '../components/ConfirmationDialog'
import ErrorPaper from '../components/ErrorPaper'
import InfoPaper from '../components/InfoPaper'
import { useAuth } from '../components/AuthContext'

const PensieveBaseUri = 'http://api.epensieve.com';

const Styles = ({
    paper: {
        width: { xl: 1240, lg: 940, md: 640, sm: 480, xs: 320 },
        minWidth: { xl: 1200, lg: 900, md: 600, sm: 480, xs: 320 },
        margin: '15px auto',
        overflow: 'hidden',
    },
    sub_paper: {
        marginLeft: '5px',
        overflow: 'hidden',
    },
    topicBar: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
    topicText: {
        fontSize: 'primary',
    },
    addThought: {
        marginRight: 1,
    },
    block: {
        display: 'block',
    },
    contentWrapper: {
        margin: '10px 0 20px 0',
        padding: '0 10px 0 10px'
    },
    thoughtHead: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
});

type Link = {
    id: number,
    title: string,
    url: string,
}

type Thought = {
    id: number,
    type: string,
    topic: string,
    text: string,
    links: Link[],
    modified: string,
}

interface TopOfMinProps extends BoxProps {
    type: string;
};

function TopOfMind( {type, ...props} : TopOfMinProps) {
    const {token, userInfo} = useAuth()!;
    const default_topic = userInfo?.default_topic || 'Something';

    const [loading, setLoading] = useState(true);
    const [remove, setRemove] = useState(false);
    const [thought, setThought] = useState<Thought | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const [newTopic, setNewTopic] = useState(default_topic);

    useEffect(() => {
        setLoading(true);
    },[type]);

    useEffect(() => {
        if (!token || !loading) return;

        fetch(`${PensieveBaseUri}/thoughts?top=${type}`, { method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
        }).then( (response) => {
            if (!response.ok) {
                return null;
            }
            return response.json();
        })
        .then((result) => {
            setThought(result);
            setLoading(false);
        })
        .catch((err) => {
            setError(err.message);
        })
    }, [loading, token, type]);

    const handleThoughtEdit = (field: string, value: any) => {
        setThought({
            ...thought!,
            [field]: value,
        });
    };
    
    const handleLinkAdd = () => {
        setThought({
            ...thought!,
            links: [{
                id: 0,
                title: '',
                url: 'http://'
            }].concat( thought!.links )
        });
    };

    const handleLinkEdit = (index: number, field: string, value: any) => {
        let link = thought!.links[index];
        thought!.links[index] = {
            ...link,
            [field]: value
        };

        setThought({
            ...thought!,
            links: thought!.links
        });
    };
    
    const handleLinkDelete = (index: number) => {
        setThought({
            ...thought!,
            links: thought!.links.filter((v, i) => (i !== index))
        });
    };

    const handleAddThought = () => {
        fetch(`${PensieveBaseUri}/thoughts`, { method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                topic: newTopic,
                text: '',
                type
            })
        }).then( (response) => {
            if (!response.ok) {
                throw new Error('Could not Create Thought, try Refreshing or Logging In');
            }
            return response.text();
        }).then( (result) => {
            setInfo('Successfully Created Thought.');
            setTimeout(() => { setInfo(null); }, 1000);

            // Set this as the new Thought
            setThought({
                id: +result, type,
                topic: newTopic,
                text:'', 
                links: [],
                modified: (new Date()).toISOString()
            });
        })
        .catch((err) => {
            setError(err.message);
        })
    };

    const handleThoughtSave = () => {
        fetch(`${PensieveBaseUri}/thoughts/${thought!.id}`, { method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                topic: thought!.topic, 
                text: thought!.text,
                type: thought!.type,
                links: thought!.links
            })
        }).then( (response) => {
            if (!response.ok) {
                throw new Error('Could not Update Thought, try Refreshing or Logging In');
            }

            setInfo('Successfully updated Thought.');
            setTimeout(() => { setInfo(null); }, 2000);

            // Move Thought to the top of the list
            setThought({...thought!, modified: (new Date()).toISOString()});
        })
        .catch((err) => {
            setError(err.message);
        })
    };

    const handleThoughtRemoval = () => {
        fetch(`${PensieveBaseUri}/thoughts/${thought!.id}`, { method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).then( (response) => {
            if (!response.ok) {
                throw new Error('Could not Delete Thought, try Refreshing or Logging In');
            }

            setInfo('Successfully removed Thought.');
            setTimeout(() => { setInfo(null); }, 2000);

            setRemove(false);
            setLoading(true);
        })
        .catch((err) => {
            setError(err.message);
        })
    };

    if (loading) {
        return (<Paper sx={Styles.paper} > <Typography> Loading Thoughts ... </Typography> </Paper>);
    }
    
    if (!thought) {
        return (
        <Paper sx={{...Styles.paper, height: '250px'}} > 
        <Grid container direction={'column'} spacing={0} alignContent='center' alignItems={'center'} sx={{padding:'30px 15px 10px 15px'}} >
            <Grid item width='inherit' textAlign={'center'} sx={{marginBottom:'20px'}} >
                <Typography fontSize={18} > You don't have any Thoughts of this type. </Typography> 
            </Grid>
            <Grid item width='inherit' sx={{margin:'10px 25px 30px 25px', padding: '0 10px 0 10px'}} >
                <TextField fullWidth variant='outlined' label='Topic' value={newTopic} onChange={(evt:any) => { setNewTopic(evt.target.value); }} /> 
            </Grid>
            <Grid item >
                <Button fullWidth variant='contained' onClick={handleAddThought} >Create Thought on this Topic</Button>
            </Grid>
        </Grid>
        </Paper>);
    }

    return (
        <Box {...props}>
            { (error) && <ErrorPaper error={error} /> }
            { (info)  && <InfoPaper  msg={info} /> }
            <Paper sx={Styles.paper} >
                <AppBar sx={Styles.topicBar} position='static' color='default' elevation={0} >
                    <Toolbar>
                        <Grid container spacing={1} alignItems='center' >
                        <Grid item>
                            {(type === 'personal') ? <PersonalIcon /> : (type === 'professional') ? <ProfessionalIcon /> : <SocialIcon /> }
                        </Grid>
                        <Grid item xs >
                            <TextField
                                fullWidth
                                variant='standard'
                                value={thought?.topic || ''}
                                onChange={(evt: any) => { handleThoughtEdit('topic', evt.target.value) }}
                                sx={Styles.topicText}
                                InputProps={{
                                    disableUnderline: true,
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item xs />
                        {(type === 'personal') ? (<>
                            <Grid item>
                                <IconButton onClick={() => {handleThoughtEdit('type', 'professional')}} >
                                    <ProfessionalIcon />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <IconButton onClick={() => {handleThoughtEdit( 'type', 'social')}} > 
                                    <SocialIcon />
                                </IconButton>
                            </Grid>
                        </>) : (type === 'professional') ? (<>
                            <Grid item>
                                <IconButton onClick={() => {handleThoughtEdit('type', 'social')}} > 
                                    <SocialIcon />
                                </IconButton>
                                <IconButton onClick={() => {handleThoughtEdit('type', 'personal')}} > 
                                    <PersonalIcon />
                                </IconButton>
                            </Grid>
                        </>) : (<>
                            <Grid item>
                                <IconButton onClick={() => {handleThoughtEdit('type', 'personal')}} > 
                                    <PersonalIcon />
                                </IconButton>
                                <IconButton onClick={() => {handleThoughtEdit('type', 'professional')}} >
                                    <ProfessionalIcon />
                                </IconButton>
                            </Grid>
                        </>)}
                        </Grid>
                    </Toolbar>
                </AppBar>
            </Paper>
            <Paper sx={Styles.paper} >
                <Grid container direction={'column'} sx={{...Styles.contentWrapper}} >
                    <Grid item sx={{margin:'5px 0 10px 0'}}>
                        <TextField 
                            fullWidth multiline rows={10} 
                            value={thought?.text || ''}
                            onChange={(evt) => { handleThoughtEdit('text', evt.target.value) }}
                        />
                    </Grid>
                    <Grid item sx={{margin:'5px 0 10px 0'}}>
                        <Paper >
                            <Grid container direction={'column'}>
                                <Grid item>
                                    <AppBar sx={Styles.topicBar} position='static' color='default' elevation={0} >
                                        <Toolbar>
                                            <Grid container spacing={1} alignItems='center' >
                                                <Grid item>
                                                    <Typography fontWeight={'bold'}>Associated Links </Typography>
                                                </Grid>
                                                <Grid item xs />
                                                <Grid item>
                                                    <IconButton onClick={handleLinkAdd} >
                                                        <AddLinkIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Toolbar>
                                    </AppBar>
                                </Grid>
                                {thought?.links.map((link: Link, index) => (
                                    <Grid item sx={{mx:'10px', my: '5px'}}>
                                        <Card>
                                            <CardContent sx={{margin: '5px 10px 15px 10px', padding: '0'}}>
                                                <TextField
                                                    fullWidth
                                                    variant='standard'
                                                    placeholder='Title'
                                                    value={link.title}
                                                    onChange={(evt) => { handleLinkEdit(index, 'title', evt.target.value); }}
                                                    sx={{py: '10px', px: '5px'}}
                                                    InputProps={{
                                                        disableUnderline: true,
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    label='Address'
                                                    size='small'
                                                    value={link.url}
                                                    onChange={(evt) => { handleLinkEdit(index, 'url', evt.target.value); }}
                                                />
                                            </CardContent>
                                            <CardActions>
                                                <Button variant='outlined' size='small' startIcon={<LaunchIcon />} onClick={() => { window.open(link.url); }} > Navigate </Button>
                                                <Button variant='outlined' size='small' color='error' startIcon={<DeleteIcon />} onClick={() => { handleLinkDelete(index); }} > Delete </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>
                    <Grid item sx={{margin: '10px 10px 0 0'}}>
                        <Grid container alignItems={'center'} spacing={2} >
                            <Grid item>
                                <Typography fontSize={'small'} > Modified: {(new Date(thought?.modified || '').toLocaleString())}</Typography>
                            </Grid>
                            <Grid item xs />
                            <Grid item>
                                <IconButton onClick={() => { handleThoughtSave() }}>
                                    <SaveIcon />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <IconButton onClick={() => { setRemove(true); }}>
                                    <DeleteIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
            <ConfirmationDialog open={remove} title={'Remove this Thought?'} 
                handleClose={ () => { setRemove(false); } }
                handleConfirm={ handleThoughtRemoval }
            >
                Are you sure you want to remove this Thought? This action is permanent and can't be undone.
            </ConfirmationDialog>
        </Box>
    )
}

export default TopOfMind